import React from 'react';
import { Check } from 'lucide-react';
import { Reveal, Stagger, StaggerItem } from './Reveal';

const programs = [
  {
    id: 1,
    name: "Starter Consultation",
    subtitle: "One-time clarity before you commit",
    duration: "One-time",
    description: "Best for you if you want professional direction without long-term commitment.",
    includes: [
      "60-minute 1:1 Zoom consultation",
      "Deep understanding of your lifestyle, work routine, food habits, and goals",
      "Basic review of recent blood reports",
      "Clear expectation setting around timelines, outcomes, and effort required",
      "Broad, practical dietary guidelines you can start implementing immediately"
    ],
    outcome: "You leave with clarity, not confusion, and a realistic direction forward.",
    featured: false
  },
  {
    id: 2,
    name: "3-Month Transformation Plan",
    duration: "3 Months",
    badge: "MOST CHOSEN",
    description: "The best balance of results, support, and value for working professionals committed to lasting change.",
    includes: [
      "60-minute in-depth Zoom/in-person consultation at the start",
      "Monthly Zoom/in-person review to reassess progress and refine strategy",
      "4 fully customised nutrition plans tailored to different phases and situations",
      "Weekly phone check-ins for tight accountability",
      "Blood marker assessments",
      "Priority WhatsApp access for quicker feedback",
      "Hands-on support during: Social events, Stressful work phases, Travel and disrupted routines",
      "Meal picture reviews with feedback on portion size, balance and goal alignment",
      "One comprehensive blood report review during the program, with nutrition based insights"
    ],
    featured: true
  },
  {
    id: 3,
    name: "6-Month Transformation Pro Plan",
    subtitle: "Maximum support & faster course correction",
    duration: "6 Months",
    description: "Best for you if you want closer guidance, quicker adjustments, and higher accountability.",
    includes: [
      "60-minute in-depth Zoom/in-person consultation at the start",
      "Monthly Zoom/in-person review to reassess progress and refine strategy",
      "7 fully customised nutrition plans tailored to different phases and situations",
      "Weekly phone check-ins for tight accountability",
      "Blood marker assessments",
      "Priority WhatsApp access for quicker feedback",
      "Hands-on support during: Social events, Stressful work phases, Travel and disrupted routines",
      "Meal picture reviews with feedback on portion size, balance and goal alignment",
      "One comprehensive blood report review during the program, with nutrition based insights"
    ],
    outcome: "Less guesswork, faster corrections, and confidence that you're doing the right things.",
    featured: false
  }
];

const ProgramCard = ({ program }) => {
  const { featured } = program;
  const headingColor = 'var(--brand-deep-plum)';
  const subtleColor = 'var(--brand-muted-grey)';
  const bodyColor = 'var(--brand-charcoal)';
  const dividerColor = '#EEEEEE';

  return (
    <StaggerItem
      className="relative"
      style={{
        background: 'white',
        border: featured ? '2px solid var(--brand-periwinkle)' : '1px solid rgba(190, 147, 212, 0.12)',
        borderRadius: '20px',
        padding: '44px',
        boxShadow: featured
          ? '0 20px 56px -16px rgba(158, 111, 192, 0.28)'
          : '0 4px 24px -8px rgba(75, 46, 93, 0.06)',
        transition: 'all 0.3s ease'
      }}
    >
      {program.badge && (
        <div
          className="absolute -top-4 left-8 px-4 py-2"
          style={{ background: 'var(--brand-warm-clay)', borderRadius: '12px', boxShadow: '0 2px 8px rgba(214, 162, 142, 0.3)' }}
        >
          <p className="text-xs font-bold tracking-wider" style={{ color: 'var(--brand-deep-plum)' }}>
            {program.badge}
          </p>
        </div>
      )}

      <div className="mb-6">
        <h3 className="heading-2 mb-2" style={{ color: headingColor }}>{program.name}</h3>
        {program.subtitle && (
          <p className="text-sm mb-2" style={{ color: subtleColor }}>{program.subtitle}</p>
        )}
      </div>

      <p className="body-regular mb-8" style={{ color: bodyColor }}>{program.description}</p>

      <div className="h-px w-full mb-8" style={{ background: dividerColor }} />

      <div className="space-y-4 mb-8">
        {program.includes.map((item) => (
          <div key={item} className="flex items-start gap-3">
            <div className="mt-1 flex-shrink-0" style={{ color: 'var(--brand-sage-green)' }}>
              <Check size={20} />
            </div>
            <p className="body-regular text-left" style={{ color: 'var(--brand-charcoal)' }}>
              {item}
            </p>
          </div>
        ))}
      </div>

      {program.outcome && (
        <div className="py-6 mb-6" style={{ borderTop: `1px solid ${dividerColor}` }}>
          <p className="body-regular italic" style={{ color: bodyColor }}>
            <strong>Outcome:</strong> {program.outcome}
          </p>
        </div>
      )}

      <a
        href={`https://wa.me/919766144233?text=Hi, I'm interested in the ${program.name}. Could you please share the pricing and details?`}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`program-enquire-btn-${program.id}`}
        className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 font-semibold transition-smooth"
        style={{
          background: 'transparent',
          color: 'var(--brand-deep-plum)',
          border: '2px solid var(--brand-deep-plum)',
          borderRadius: '12px',
          minHeight: '56px'
        }}
      >
        Enquire on WhatsApp
      </a>
    </StaggerItem>
  );
};

export const ProgramsSection = () => {
  return (
    <section
      id="programs"
      className="section-padding"
      style={{ background: 'var(--brand-purple-bg)' }}
    >
      <div className="container-custom">
        <div className="max-w-7xl mx-auto">
          <Reveal className="text-center mb-16">
            <h2 className="heading-1 text-balance" style={{ color: '#FFFFFF' }}>Our Nutrition Programmes</h2>
            <p className="body-large mt-6" style={{ color: 'rgba(255, 255, 255, 0.88)' }}>
              Choose the right plan for your transformation journey
            </p>
          </Reveal>

          <Stagger className="flex flex-col gap-8 max-w-4xl mx-auto" stagger={0.14}>
            {programs.map((program) => (
              <ProgramCard key={program.id} program={program} />
            ))}
          </Stagger>
        </div>
      </div>
    </section>
  );
};
